import React, { FC, useEffect, useMemo, useState } from "react";

import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { CommonColors } from "../../../types/colors";
import { AnchorCard } from "../../cards/anchor-card";
import { PaginationIndicator } from "../../pagination/pagination-indicator";
import { PaginationIndicatorColor } from "../../pagination/pagination-indicator/types";

export interface HeroBannerAnchorsProps {
  color: CommonColors;
  anchors: (Queries.AnchorDataFragment | null)[];
}

export const HeroBannerAnchors: FC<HeroBannerAnchorsProps> = ({ color, anchors }) => {
  const breakpoints = useBreakpoints();
  const [pageIndex, setPageIndex] = useState(0);

  const anchorsCount = anchors?.length ?? 0;
  const visibleAnchors = useMemo(() => {
    let responsiveMaxVisibleItems = 1;
    if (breakpoints.sm) responsiveMaxVisibleItems = 2;
    if (breakpoints.lg) responsiveMaxVisibleItems = 3;
    if (breakpoints.xl) responsiveMaxVisibleItems = 4;
    if (breakpoints["2xl"]) responsiveMaxVisibleItems = 5;

    return Math.min(anchorsCount, responsiveMaxVisibleItems);
  }, [breakpoints, anchorsCount]);

  const pagesCount = Math.ceil(anchorsCount / visibleAnchors);

  const startIndexForCurrentPage = useMemo(() => {
    const endIndexForCurrentPage = Math.min((pageIndex + 1) * visibleAnchors, anchorsCount);
    const startIndexForCurrentPage = endIndexForCurrentPage - visibleAnchors;
    if (!anchorsCount) return 0;
    return startIndexForCurrentPage;
  }, [pageIndex, visibleAnchors, anchorsCount]);

  const itemsLeftOffsetPercent = startIndexForCurrentPage * (100 / visibleAnchors);

  useEffect(() => {
    setPageIndex((currentIndex) => (currentIndex > pagesCount - 1 ? pagesCount - 1 : currentIndex));
  }, [pagesCount]);

  return (
    <div className="w-full px-8 -mt-10 z-20">
      <div className="w-full overflow-x-hidden">
        <div
          className="flex duration-500"
          style={{
            transform: `translateX(-${itemsLeftOffsetPercent}%)`,
          }}
        >
          {breakpoints.xs &&
            anchors?.map((anchor, i) => {
              const isInPage =
                i < startIndexForCurrentPage + visibleAnchors && i >= startIndexForCurrentPage;
              return anchor ? (
                <AnchorCard
                  {...anchor}
                  className="transition-all duration-500"
                  style={{
                    flex: `0 0 ${100 / visibleAnchors}%`,
                    opacity: isInPage ? 100 : 0,
                    scale: isInPage ? "100%" : 0,
                  }}
                  key={i}
                  color={color === CommonColors.Light ? CommonColors.Neutral : CommonColors.Light}
                />
              ) : null;
            })}
        </div>
      </div>
      <div className="absolute bottom-1 lg:bottom-4 left-0 w-full">
        {pagesCount > 1 && breakpoints.xs && (
          <PaginationIndicator
            color={
              [CommonColors.Light, CommonColors.Neutral].includes(color)
                ? PaginationIndicatorColor.Primary
                : PaginationIndicatorColor.Light
            }
            totalItems={pagesCount}
            activeItem={pageIndex}
            onClickItem={setPageIndex}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
};
